import { useEffect, useState } from "react";
import { Member } from "../models";
import { DataStore } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFilterAttendanceHistory,
  selectFilterChapterInvolvement,
  selectFilterCommitteeCredit,
  selectFilterNegative,
  selectFilterPayment,
  selectFilterSocials,
  selectFilterVivo,
  selectFilterWorkshops,
  setFilterError,
  setFilterResult,
} from "../features/search/slice";
import parseModel from "../util/parseModel";
import { isBoolean, toArray } from "lodash";

export function useFilterMember() {
  //--- state values
  const [skipQuery, setSkipQuery] = useState(true);
  const dispatch = useDispatch();
  const vivoFilter = useSelector(selectFilterVivo);
  const committeeCreditFilter = useSelector(selectFilterCommitteeCredit);
  const attendanceHistoryFilter = useSelector(selectFilterAttendanceHistory);
  const socialsFilter = useSelector(selectFilterSocials);
  const workshopsFilter = useSelector(selectFilterWorkshops);
  const negativeFilter = useSelector(selectFilterNegative);
  const paymentFilter = useSelector(selectFilterPayment);
  const chapterInvolvementFilter = useSelector(selectFilterChapterInvolvement);

  //--- dynamic query helpers
  const qVivo = (number) => (c) => c.vivo.ge(number);
  const qCommitteeCredit = (number) => (c) => c.committeeCredit.ge(number);
  const qAttendanceHistory = (number) => (c) => c.attendanceHistory.ge(number);
  const qSocials = (number) => (c) => c.socials.ge(number);
  const qWorkshops = (number) => (c) => c.workshops.ge(number);
  const qNegative = (number) => (c) => c.negative.ge(number);
  const qPayment = (boolean) => (c) => {
    if (boolean === true) {
      return c.payment.eq(true);
    } else {
      return c.payment.ne(true);
    }
  };
  const qChapterInvolvement = (number) => (c) =>
    c.chapterInvolvement.ge(number);

  // get member
  useEffect(() => {
    if (skipQuery) return;
    queryMembers();
  }, [skipQuery]);

  const generateQueryClaws = (c) => {
    var claws = [];
    let has = (v) => v > 0;

    //push claws to array
    has(vivoFilter) && claws.push(qVivo(vivoFilter));
    has(committeeCreditFilter) &&
      claws.push(qCommitteeCredit(committeeCreditFilter));
    has(attendanceHistoryFilter) &&
      claws.push(qAttendanceHistory(attendanceHistoryFilter));
    has(socialsFilter) && claws.push(qSocials(socialsFilter));
    has(workshopsFilter) && claws.push(qWorkshops(workshopsFilter));
    has(negativeFilter) && claws.push(qNegative(negativeFilter));
    has(isBoolean(paymentFilter)) && claws.push(qPayment(paymentFilter));
    has(chapterInvolvementFilter) &&
      claws.push(qChapterInvolvement(chapterInvolvementFilter));

    return claws;
  };

  const queryMembers = async () => {
    //prepare query claws
    const claws = generateQueryClaws();
    if (claws.length === 0) {
      console.error("queryMembers: must at least have one filter claw");
      return;
    }

    //start query
    try {
      const members = await DataStore.query(
        Member,
        (c) =>
          c.and((c) => {
            const clawsWithC = claws.map((func) => func(c));
            return [...clawsWithC];
          }),
        {
          page: 0,
          limit: 200,
        }
      );
      dispatch(setFilterResult(toArray(parseModel(await members))));
    } catch (error) {
      const errorStr = JSON.stringify(error);
      dispatch(setFilterError(errorStr));
      console.error("queryMembers error: ", errorStr);
    }
    setSkipQuery(true);
  };

  const startQuery = () => {
    setSkipQuery(false);
  };

  return { startQuery };
}
